"use client";

import Image from "next/image";
import { usePathname } from "next/navigation";

import DatamuniBadgeIcon from "@/public/common/svg/datamuni-badge-icon.svg";
import DatamuniBadgeIconDark from "@/public/common/svg/datamuni-badge-icon-dark.svg";

import styles from "./Auth.module.scss";
import { Theme } from "@constants/enums";
import useWindowDimensions from "@utils/useWindowDimensions";
import { NEWS_AUTH_URL } from "@api/apiRoutes";

import GoogleIcon from "@public/common/svg/google-icon.svg";
import TwitterIcon from "@public/common/svg/twitter-icon.svg";

interface AuthProps {
    theme?: Theme;
}

interface AuthProvider {
    provider: string;
    iconPath: any;
}

const authProviders: AuthProvider[] = [
    {
        provider: "google",
        iconPath: GoogleIcon,
    },
    {
        provider: "twitter",
        iconPath: TwitterIcon,
    },
];

const Auth: React.FC<AuthProps> = () => {
    const theme = localStorage.getItem("theme");

    const dimensions = useWindowDimensions();
    const pathname = usePathname();

    const handleLogin = (provider: string) => {
        if (typeof window === "undefined") return;
        const redirectUri = `${
            window.location.origin
        }/auth/callback?redirect=${encodeURIComponent(pathname)}`;

        const authUrl = `${NEWS_AUTH_URL}?provider=${provider}&redirectUri=${encodeURIComponent(
            redirectUri,
        )}`;

        window.location.href = authUrl;
    };

    return (
        <section className={styles?.loginCard} data-theme={theme}>
            <div className={styles?.iconContainer}>
                {theme === Theme.LIGHT ? (
                    <Image
                        src={"/common/svg/datamuni-badge-icon.svg"}
                        alt="Datamuni Icon"
                        className={styles?.icon}
                        width={60}
                        height={60}
                    />
                ) : dimensions?.width < 768 && theme === Theme.DARK ? (
                    <Image
                        src={"/common/svg/datamuni-badge-icon-dark.svg"}
                        alt="Datamuni Icon"
                        className={styles?.icon}
                        width={60}
                        height={60}
                    />
                ) : (
                    <Image
                        src={"/common/svg/datamuni-badge-icon.svg"}
                        alt="Datamuni Icon"
                        className={styles?.icon}
                        width={60}
                        height={60}
                    />
                )}
            </div>
            <div className={styles["header-container"]} data-theme={theme}>
                <div className={styles["header-wrapper"]} data-theme={theme}>
                    Welcome to <span data-theme={theme}>Datamuni</span>
                </div>

                <div className={styles["login-access-text"]} data-theme={theme}>
                    Login to access insights and make informed decisions
                </div>
            </div>
            <div className={styles?.loginButtonContainer} data-theme={theme}>
                {authProviders.map((authProvider, index) => {
                    const ProviderIcon = authProvider.iconPath;
                    return (
                        <button
                            className={`${styles.authButton}`}
                            onClick={() => handleLogin(authProvider.provider)}
                            key={index}
                        >
                            <div
                                className={styles.authIcon}
                                data-theme={
                                    authProvider.provider === "twitter" && theme
                                }
                            >
                                <ProviderIcon
                                    className={styles.authIcon}
                                    data-theme={
                                        theme === Theme.DARK ? "dark" : "light"
                                    }
                                />
                            </div>
                            <span
                                className={styles["login-text"]}
                                data-theme={theme}
                            >
                                Sign in with{" "}
                                <span
                                    className={styles["provider-text"]}
                                    data-theme={theme}
                                >
                                    {" "}
                                    {authProvider.provider}{" "}
                                </span>{" "}
                            </span>
                        </button>
                    );
                })}
            </div>
        </section>
    );
};

export default Auth;
